import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { parseLocalISODate } from '@/lib/dateUtils';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AccountSelect } from '@/components/accounting/ui/account-select';
import { useShowAccountCurrency } from '@/components/accounting/ui/useAccountLabelHelpers';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { FileAttachments, type FileAttachment } from '@/components/ui/file-attachments';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useChartOfAccounts } from '@/hooks/useChartOfAccounts';
import type { ChartOfAccount } from '@/types/financial-accounts';
import { useBusinesses } from '@/hooks/useBusinesses';
import { usePersonIndex } from '@/hooks/usePeople';
import { useProjects } from '@/hooks/useProjects';
import { useFunds } from '@/hooks/useFunds';
import { FundSelect } from '@/components/accounting/ui/fund-select';
import { useCreateExpense, useUpdateExpense, useUpdateExpenseHeader, buildExpenseCreateBody, type Expense, type ExpenseLineItem } from '@/hooks/useExpenses';
import { recurringPayloadSchemaByType, type RecurringTemplateWire } from '@releve/shared';
import { DangerZone } from '@/components/ui/danger-zone';
import { RecurringSection } from './recurring/RecurringSection';
import {
  recurringFormFields,
  recurringFormRefinement,
  resolveRecurringDefaults,
  type RecurringFormFields,
} from './recurring/recurringFormFields';
import { useRecurringTemplateSubmit } from './recurring/useRecurringTemplateSubmit';
import { buildRecurringPayload, hydrateExpenseFromPayload } from '@/lib/recurringPayload';
import { suggestTemplateName, todayInOrgZone, type RecurrenceFormSchemaContext } from '@/lib/recurrence';
import { InvoiceLineItems } from './InvoiceLineItems';
import { DeclaredVatBanner } from './DeclaredVatBanner';
import { declaredSettlementIdOf } from '@/lib/vatDeclared';
import { useSuspenseAccountBalance } from '@/hooks/useSuspenseAccountBalance';
import { useCarbonTracking } from '@/hooks/useCarbonTracking';
import { useUploadTransactionAttachments, useTransactionAttachments, useDeleteTransactionAttachment } from '@/hooks/useTransactionAttachments';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, TriangleAlert } from 'lucide-react';
import { mapParsedDocumentToExpenseForm, convertLineItemAmounts, buildConversionMemoNote } from '@/lib/parsedDocumentMapper';
import { useParsedDocumentConversion } from '@/hooks/useParsedDocumentConversion';
import { resolveTaxTreatment, toTaxRateType } from '@/lib/taxCalculations';
import { isForeignCurrency, resolveAccountCurrency } from '@/lib/currency';
import { lineAccountCurrencyMatches } from '@/lib/lineAccountCurrency';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';
import { useSaveBudgetCategoryEntries, useBudgetCategoryEntriesByTransaction } from '@/hooks/budgets/useBudgetCategoryEntries';
import { supabase } from '@/integrations/supabase/client';
import type { TaxRateType } from '@/lib/taxCalculations';
import type { NlWkrCategory } from '@releve/shared';
import { useNlWkrSourceCategories } from '@/hooks/useNlWkr';
import type { ParsedDocument } from '@/types/parsedDocument';
import { hasUserLineDirtyLeaf } from '@/lib/formDirty';

const EMPTY_ACCOUNTS: ChartOfAccount[] = [];

// Only a `true` dirtyFields leaf, outside the InvoiceLineItems shadow fields,
// means the user changed something (lib/formDirty).
const hasUserDirtyLeaf = hasUserLineDirtyLeaf;

// Line-item schema shared with the bulk expenses grid — lives in its own
// module (expenseLineItemSchema.ts) so this component file keeps fast refresh.
import { lineItemSchema } from './expenseLineItemSchema';

export type { ExpenseLineItemFormData } from './expenseLineItemSchema';

const formSchema = z.object({
  payee_type: z.enum(['business', 'person'], { required_error: 'Please select payee type' }),
  business_id: z.string().optional(),
  business_name: z.string().optional(), // For custom business name input
  person_id: z.string().optional(),
  expense_date: z.string().min(1, 'Expense date is required'),
  reference: z.string().optional(),
  memo: z.string().optional(),
  payment_account_id: z.string().min(1, 'Payment account is required'),
  project_id: z.string().optional(),
  fund_id: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, 'At least one line item is required'),
  // "Make this recurring" — form-local toggle + the schedule (see RecurringSection).
  ...recurringFormFields,
}).refine((data) => {
  if (data.payee_type === 'business') {
    return (data.business_id && data.business_id.length > 0) || (data.business_name && data.business_name.trim().length > 0);
  } else {
    return data.person_id && data.person_id.length > 0;
  }
}, {
  message: 'Please select or enter a business name, or select a person',
  path: ['payee_type'],
});

type FormData = z.infer<typeof formSchema>;

/**
 * The resolver schema — the base shape plus the context-dependent schedule
 * rules (org-today, a start locked after the first run); the context is read
 * through `getContext` at validation time so the schema is built once.
 */
const buildFormSchema = (getContext: () => RecurrenceFormSchemaContext) =>
  formSchema.superRefine(recurringFormRefinement(getContext));

/**
 * Public form data shape for callers that want to pre-fill specific fields
 * (e.g., the create-from-import dialog). Exported as a Partial so callers
 * only need to provide the fields they have.
 */
export type ExpenseFormData = FormData;

interface ExpenseResult {
  expense_id: string;
  ledger_transaction_id: string;
  success: boolean;
}

interface CreateExpenseFormProps {
  onSuccess?: () => void;
  onSubmitReady?: (submitHandler: () => void) => void;
  /**
   * Called whenever the form's create/update mutation transitions in or out of
   * the pending state. Used by parent dialogs (e.g. CreateExpenseFromImportDialog)
   * to disable the dialog's submit button while a save is in flight, preventing
   * double-submit. Optional — if omitted, callers don't get pending state and
   * must rely on the form's own button to gate clicks.
   */
  onSubmittingChange?: (isPending: boolean) => void;
  /**
   * Called whenever the form's dirtiness changes (RHF isDirty, or a newly
   * added attachment). Parent dialogs use it to gate FullscreenFormDialog's
   * `skipCloseConfirmation` so a pristine form closes without the
   * "Discard changes?" prompt.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  onExpenseCreated?: (result: ExpenseResult) => void | Promise<void>; // Called with expense creation result
  initialAttachments?: Array<{ file: File; name: string }>; // Pre-populate attachments
  initialParsedData?: ParsedDocument | null; // AI-extracted document data to pre-populate form
  /**
   * Direct form-data pre-fill — used by the "Create from bank import" dialog
   * to populate fields the ParsedDocument path can't handle (payment_account_id,
   * resolved business_id, full line_items with explicit unit_price).
   *
   * Mutually exclusive with initialParsedData. The dialog memoizes the value
   * by item ID so the reset effect doesn't re-fire on unrelated re-renders.
   */
  initialFormData?: Partial<FormData>;
  /**
   * Display currency for line-item amounts. Passed by the "Create from bank
   * import" dialog so a foreign-but-same-as-account import (e.g. a USD line into
   * a USD account) renders as "$" rather than the org base currency. Omitted
   * elsewhere → amounts fall back to the org currency. Display-only; posting is
   * currency-agnostic (amounts post as-is against the chosen accounts).
   */
  currency?: string | null;
  expenseId?: string;
  expense?: Expense; // The expense to edit
  /**
   * Show the "Make this recurring" section (feature-flag gated inside). Only
   * the Expenses tab, the quick-add panel and the recurring page pass it —
   * the inbox / import / impact hosts do not.
   */
  allowRecurring?: boolean;
  /** Open with the recurring box already ticked (the "New recurring…" menu). */
  defaultRecurring?: boolean;
  /** Template-edit mode: hydrate from the template; saving PATCHes it in place. */
  recurringTemplate?: RecurringTemplateWire | null;
  /** Fires when the user ticks/unticks the box — hosts swap the footer label. */
  onRecurringChange?: (enabled: boolean) => void;
  /** After a recurring CREATE the form navigates to `/accounting/recurring` unless `false`. */
  navigateOnSuccess?: boolean;
  /** Template-edit mode: the DangerZone's action — the host owns the confirm + delete. */
  onRequestDelete?: () => void;
  /**
   * Clone mode ("Duplicate" / "Make recurring" from an expense's expanded
   * row): hydrate payee, lines, accounts, tax, payment account, project, fund
   * and memo from an EXISTING expense, but save as a NEW one — no id, today's
   * date, an empty reference. Attachments, carbon and budget tags are not
   * copied. Mutually exclusive with `expenseId` / `recurringTemplate`.
   */
  cloneFrom?: Expense | null;
  /**
   * Preset the project on a NEW expense (the project dashboard's Add menu).
   * Seeds `defaultValues` only: ignored in edit mode, and any source that
   * carries its own project — a recurring template, `initialFormData`, a
   * clone — still overrides it (they reset over the defaults). The user can
   * change or clear it.
   */
  defaultProjectId?: string;
}

export const CreateExpenseForm = ({
  onSuccess,
  onSubmitReady,
  onSubmittingChange,
  onDirtyChange,
  onExpenseCreated,
  initialAttachments,
  initialParsedData,
  initialFormData,
  currency,
  expenseId,
  expense,
  allowRecurring = false,
  defaultRecurring = false,
  recurringTemplate = null,
  cloneFrom = null,
  onRecurringChange,
  navigateOnSuccess,
  onRequestDelete,
  defaultProjectId,
}: CreateExpenseFormProps) => {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const updateExpenseHeader = useUpdateExpenseHeader();
  const uploadAttachments = useUploadTransactionAttachments();
  const deleteAttachment = useDeleteTransactionAttachment();
  const isEditMode = !!expenseId;
  const isTemplateMode = !!recurringTemplate;
  const { submit: submitRecurringTemplate, isPending: isRecurringPending } = useRecurringTemplateSubmit();

  // The ONE "today" the schedule uses — the org's calendar day (docs/DATES.md).
  const orgToday = useMemo(
    () => todayInOrgZone(selectedOrganization?.timezone),
    [selectedOrganization?.timezone],
  );

  // Template-edit mode: the stored payload is jsonb — re-validate before hydrating.
  const templatePayload = useMemo(() => {
    if (!recurringTemplate) return null;
    const parsed = recurringPayloadSchemaByType.expense.safeParse(recurringTemplate.payload);
    if (!parsed.success) {
      captureException(new Error('Recurring expense template payload failed validation'), {
        context: 'Expense recurring template hydrate',
        templateId: recurringTemplate.id,
        issues: parsed.error.issues.map((i) => i.path.join('.')).join(','),
      });
      return null;
    }
    return parsed.data;
  }, [recurringTemplate]);

  // Read at validation time by the resolver (see buildFormSchema).
  const recurrenceContextRef = useRef<RecurrenceFormSchemaContext>({ orgToday, documentType: 'expense' });
  recurrenceContextRef.current = {
    orgToday,
    documentType: 'expense',
    originalStartDate: recurringTemplate?.start_date ?? null,
    startLocked: (recurringTemplate?.occurrences_generated ?? 0) > 0,
  };
  const resolverSchema = useMemo(() => buildFormSchema(() => recurrenceContextRef.current), []);

  // Declared VAT (docs/TAX.md "Edit guard"): once a VAT line of the posting
  // journal sits on a filed return, amounts, line items, tax, the date and
  // the payment account are frozen — the full update RPC would delete and
  // re-post every line and be refused. Derived from the detail the host
  // already loads (`entries` carry `vat_settlement_id`), so the `?view=`
  // deep link and the grid's number-cell click are gated the same way.
  const declaredSettlementId = isEditMode
    ? declaredSettlementIdOf(expense?.ledger_transaction?.entries)
    : null;
  const isDeclared = !!declaredSettlementId;

  // Store expenseId in ref to prevent stale closures during state transitions
  const expenseIdRef = useRef(expenseId);
  expenseIdRef.current = expenseId;

  const { data: accounts, isLoading: isLoadingAccounts } = useChartOfAccounts(selectedOrganization?.id);
  const showAccountCurrency = useShowAccountCurrency(accounts);
  const { data: businesses, isLoading: isLoadingBusinesses } = useBusinesses(selectedOrganization?.id);
  const { data: people, isLoading: isLoadingPeople } = usePersonIndex(selectedOrganization?.id, { sortByName: true });
  const { data: projects, isLoading: isLoadingProjects } = useProjects(selectedOrganization?.id);
  const { data: funds, isLoading: isLoadingFunds } = useFunds(selectedOrganization?.id);
  const { accountId: suspenseAccountId } = useSuspenseAccountBalance(selectedOrganization?.id);
  // Load existing attachments if editing
  const { data: existingAttachments } = useTransactionAttachments(
    expense?.ledger_transaction_id
  );

  const isLoadingData = isLoadingAccounts || isLoadingBusinesses || isLoadingPeople || isLoadingProjects || isLoadingFunds;

  // File attachments state
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // Budget category tagging
  const [budgetCategoriesByLineItem, setBudgetCategoriesByLineItem] = useState<Record<number, string[]>>({});
  // Tracks whether the operator has changed a budget tag this session. Kept
  // separate from `budgetCategoriesByLineItem` (which is also populated by the
  // edit-load effect) so it reports dirtiness only on real user edits.
  const [budgetTagsDirty, setBudgetTagsDirty] = useState(false);
  const saveBudgetCategoryEntries = useSaveBudgetCategoryEntries();
  const { data: existingBudgetTags } = useBudgetCategoryEntriesByTransaction(
    isEditMode ? expense?.ledger_transaction_id : undefined
  );
  // NL WKR: existing classifications keyed by invoice_line_items.id — merged
  // into the edit-load reset by LINE ID (never array position). Disabled (and
  // instantly settled) outside gated NL orgs.
  const { data: wkrByLineId, isLoading: isLoadingWkr } = useNlWkrSourceCategories(
    selectedOrganization?.id,
    'expense',
    isEditMode ? expenseId : cloneFrom?.id,
  );

  // Recurring defaults go through `defaultValues` (never a mount-time
  // setValue — hasUserDirtyLeaf must stay false until the user acts).
  const recurringDefaults: RecurringFormFields = resolveRecurringDefaults({
    documentType: 'expense',
    orgToday,
    template: recurringTemplate,
    defaultRecurring,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(resolverSchema),
    defaultValues: {
      payee_type: 'business' as 'business' | 'person',
      business_id: '',
      business_name: '',
      person_id: '',
      // `expenses.expense_date` is a plain `date` column — a calendar day,
      // never an instant. Today's LOCAL day (never `.toISOString()`). In
      // template-edit mode the (disabled) field shows the schedule's start.
      expense_date: recurringTemplate?.start_date ?? format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      memo: '',
      payment_account_id: '',
      project_id: (!expenseId && defaultProjectId) || '',
      fund_id: '',
      line_items: [
        {
          expense_account_id: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          tax_rate: 0 as TaxRateType,
          tax_inclusive: false,
          is_reverse_charge: false,
          exemption_reason: '',
          exemption_reason_other: '',
          // Placeholder 0 — InvoiceLineItems fills the org default rate.
          tax_rate_unset: true,
        },
      ],
      // Template-edit mode: the document half comes from the stored payload.
      ...(templatePayload ? hydrateExpenseFromPayload(templatePayload) : {}),
      ...recurringDefaults,
    },
  });

  const payeeType = form.watch('payee_type');
  // While ticked, the expense date is set by the schedule (field disabled,
  // value kept so the base schema passes; the payload strip drops it).
  const recurringEnabled = form.watch('recurring_enabled');

  // Watch line_items so carbon tracking remaps when the form populates on
  // edit (form.reset may land after the carbon fetch completes).
  const watchedLineItems = form.watch('line_items');
  // A payment account outside the org currency (a US account in a EUR org)
  // carries no tax: the grid seeds none and submit refuses any.
  const watchedPaymentAccountId = form.watch('payment_account_id');
  const foreignPayment = isForeignCurrency(
    accounts?.find((a) => a.id === watchedPaymentAccountId)?.currency,
    selectedOrganization?.currency,
  );
  const carbonLineItemAccountIds = useMemo(
    () => watchedLineItems?.map(item => item?.expense_account_id) ?? [],
    [watchedLineItems],
  );

  // Convert businesses to SearchableSelect options format
  const businessOptions = useMemo(() => {
    return businesses?.map(business => ({
      value: business.id,
      label: business.name
    })) || [];
  }, [businesses]);

  // Carbon tracking integration
  const {
    carbonDataByLineItem,
    updateCarbonData,
    clearCarbonData,
    saveCarbonEmissions,
    hasManualCarbonEdits,
  } = useCarbonTracking({
    organizationId: selectedOrganization?.id || '',
    transactionId: expense?.ledger_transaction_id,
    lineItemAccountIds: carbonLineItemAccountIds,
  });

  // Load initial attachments from props (for inbox integration)
  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0 && !expenseId) {
      const transformedAttachments = initialAttachments.map((attachment, index) => ({
        id: `initial-${index}`,
        file: attachment.file,
        name: attachment.name,
      }));
      setAttachments(transformedAttachments);
    }
  }, [initialAttachments, expenseId]);

  // When the scanned document is denominated in a foreign currency (e.g. a EUR
  // receipt for a purchase settled on a USD card), look up the ECB rate so the
  // prefill below can populate ESTIMATED org-currency amounts. Skipped when
  // initialFormData is set — that path takes precedence over parsed data.
  const documentConversion = useParsedDocumentConversion({
    parsedData: initialParsedData,
    targetCurrency: selectedOrganization?.currency,
    enabled: !!initialParsedData && !expenseId && !initialFormData,
  });
  const [conversionBanner, setConversionBanner] = useState<
    | { type: 'estimated'; docCurrency: string; rate: number; rateDate?: string }
    | { type: 'unavailable'; docCurrency: string }
    | null
  >(null);
  // Read during render so the RHF formState proxy tracks isDirty; the prefill
  // effect checks it imperatively before applying a late (post-rate) reset.
  const { isDirty: isFormDirty, dirtyFields } = form.formState;
  // Once-guard: the prefill may wait on the rate query, so the effect re-runs;
  // each initialParsedData object (memoized by callers) is applied exactly once.
  const appliedParsedDataRef = useRef<ParsedDocument | null>(null);

  // Pre-populate form from AI-extracted document data (for inbox integration).
  // With a foreign-currency document the reset is held until the exchange-rate
  // lookup settles, then amounts are converted (or left raw with a warning
  // banner when no rate is available).
  useEffect(() => {
    if (!initialParsedData || expenseId || initialFormData) return;
    if (appliedParsedDataRef.current === initialParsedData) return;
    if (documentConversion.status === 'pending') return;

    const mappedData = mapParsedDocumentToExpenseForm(initialParsedData);
    if (!mappedData) return;

    appliedParsedDataRef.current = initialParsedData;

    // The rate arrived after the user already started editing — don't clobber
    // their input (and don't claim a conversion happened).
    if (isFormDirty) return;

    let lineItems = mappedData.line_items;
    let memo = mappedData.memo || '';
    if (documentConversion.status === 'converted' && documentConversion.rate !== undefined) {
      lineItems = convertLineItemAmounts(lineItems, documentConversion.rate);
      const note = buildConversionMemoNote(
        initialParsedData.total,
        documentConversion.docCurrency!,
        documentConversion.rate,
        documentConversion.rateDate,
      );
      memo = memo ? `${memo}\n${note}` : note;
      setConversionBanner({
        type: 'estimated',
        docCurrency: documentConversion.docCurrency!,
        rate: documentConversion.rate,
        rateDate: documentConversion.rateDate,
      });
    } else if (documentConversion.status === 'unavailable') {
      setConversionBanner({ type: 'unavailable', docCurrency: documentConversion.docCurrency! });
    }

    form.reset({
      ...form.getValues(), // Keep existing defaults
      payee_type: 'business',
      business_name: mappedData.business_name || '',
      business_id: '', // User may need to select/create
      expense_date: mappedData.expense_date || form.getValues('expense_date'),
      reference: mappedData.reference || '',
      memo,
      line_items: lineItems,
    });
  }, [
    initialParsedData,
    expenseId,
    initialFormData,
    documentConversion.status,
    documentConversion.rate,
    documentConversion.rateDate,
    documentConversion.docCurrency,
    isFormDirty,
    form,
  ]);

  // Pre-populate form from direct form-data overrides (for the create-from-import
  // dialog). Mutually exclusive with initialParsedData — callers must pass one or
  // the other. The dialog memoizes initialFormData by import row id so this effect
  // does not re-fire on unrelated re-renders.
  useEffect(() => {
    if (initialFormData && !expenseId) {
      if (process.env.NODE_ENV !== 'production' && initialParsedData) {
        console.warn(
          '[CreateExpenseForm] Both initialFormData and initialParsedData were provided. ' +
            'initialFormData takes precedence. Callers should pass exactly one source.',
        );
      }
      form.reset({
        ...form.getValues(),
        ...initialFormData,
      });
    }
  }, [initialFormData, initialParsedData, expenseId, form]);

  // Notify parent dialogs when the create/update mutation transitions in or
  // out of the pending state. Used to gate the dialog's submit button so a
  // user can't double-click and create two expenses for the same import row.
  const isMutationPending =
    createExpense.isPending || updateExpense.isPending || updateExpenseHeader.isPending || isRecurringPending;
  useEffect(() => {
    onSubmittingChange?.(isMutationPending);
  }, [isMutationPending, onSubmittingChange]);

  // Report dirtiness (form fields or newly added attachments — existing
  // attachments loaded for edit don't count) so parent dialogs can skip the
  // "Discard changes?" prompt when the form is pristine. Uses dirtyFields
  // rather than the aggregate isDirty: InvoiceLineItems fills the org default
  // tax rate onto placeholder lines (`tax_rate_unset`) and clears that flag
  // with `shouldDirty: false`, but RHF still recomputes isDirty against
  // defaultValues (which hold the placeholder `tax_rate: 0` + flag), so
  // isDirty reads true before the user has touched anything.
  // Dirtiness also covers the two editable states that live outside RHF —
  // staged attachments, budget-category tags, and carbon data — so an edit
  // touching only those still arms the host's "Discard changes?" prompt.
  const hasNewAttachments = attachments.some((a) => a.file);
  const hasUserEdits = hasUserDirtyLeaf(dirtyFields);
  // Coerce to a strict boolean — onDirtyChange's contract is boolean, and
  // the `||` chain would otherwise forward a falsy non-boolean tail.
  const hasUnsavedEdits = Boolean(hasUserEdits || hasNewAttachments || budgetTagsDirty || hasManualCarbonEdits);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedEdits);
  }, [hasUnsavedEdits, onDirtyChange]);

  // Render-mirrored ref so the edit-load effect below can read the CURRENT
  // dirtiness when it fires, without listing dirty-state in its deps (which
  // would re-run the reset every time dirtiness flips).
  const hasUnsavedEditsRef = useRef(hasUnsavedEdits);
  hasUnsavedEditsRef.current = hasUnsavedEdits;

  // Load existing attachments into state when they're fetched (for editing)
  useEffect(() => {
    if (existingAttachments && existingAttachments.length > 0) {
      const transformedAttachments = existingAttachments.map((attachment) => ({
        id: attachment.id,
        existingFile: attachment.file,
      }));
      setAttachments(transformedAttachments);
    }
  }, [existingAttachments]);

  // Last expense id the edit-load effect below has reset the form for.
  // Guards against LIVE `expense` data: hosts pass useExpenseById results,
  // so a background refetch (window focus, cache invalidation) hands the
  // effect a NEW object for the SAME expense — while the user has unsaved
  // edits, an unconditional form.reset would wipe their in-progress input.
  // When the form is PRISTINE, a same-id refetch DOES re-apply the fresh
  // data (e.g. reopening a just-saved expense must not pin stale cache).
  const loadedExpenseIdRef = useRef<string | null>(null);

  // Reset form when switching from edit mode to create mode
  // This prevents stale data from appearing when user opens "New Expense" after editing
  const prevExpenseIdRef = useRef(expenseId);
  useEffect(() => {
    // If we had an expenseId before but now we don't, reset the form
    if (prevExpenseIdRef.current && !expenseId) {
      form.reset();
      setAttachments([]);
      setBudgetCategoriesByLineItem({});
      setBudgetTagsDirty(false);
      clearCarbonData();
      // Allow a later re-open of the same expense to load fresh data.
      loadedExpenseIdRef.current = null;
    }
    prevExpenseIdRef.current = expenseId;
  }, [expenseId, form, clearCarbonData]);

  // Handle deletion of existing attachments
  const handleDeleteExisting = useCallback(async (attachmentId: string) => {
    if (!expense?.ledger_transaction_id || !selectedOrganization?.id) return;
    try {
      await deleteAttachment.mutateAsync({
        organizationId: selectedOrganization.id,
        attachmentId,
        transactionId: expense.ledger_transaction_id,
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  }, [expense?.ledger_transaction_id, selectedOrganization?.id, deleteAttachment]);

  // Use a ref to hold the latest submit function to avoid stale closures
  const onSubmitRef = useRef<(data: FormData) => Promise<void>>(undefined);

  // Synchronous re-entrancy guard against double-submit. A ref (not the
  // React `isMutationPending` state) because rapid double-clicks — or Enter
  // pressed twice — fire before React re-renders the pending flag, and
  // several hosts render the FullscreenFormDialog footer submit button
  // without gating it on `isSubmitting`. Set/reset in the stable `onSubmit`
  // wrapper below so every invocation path is covered.
  const isSubmittingRef = useRef(false);

  onSubmitRef.current = async (data: FormData) => {
    if (!selectedOrganization?.id) return;

    // Single-currency gate: every ledger leg of the expense must be
    // denominated in the payment account's currency (account currency, org
    // base when unset). Cross-currency movement only happens through the FX
    // clearing transfer flow — never by posting a raw amount across
    // currencies. The DB-level validate_transaction_balance trigger enforces
    // the same invariant; this check fails fast with a clearer message.
    const orgCurrency = selectedOrganization.currency;
    const paymentAccount = accounts?.find((a) => a.id === data.payment_account_id);
    const paymentCurrency = resolveAccountCurrency(paymentAccount, orgCurrency);
    const hasCurrencyMismatch = data.line_items.some((item) => {
      const lineAccount = accounts?.find((a) => a.id === item.expense_account_id);
      return !lineAccountCurrencyMatches(lineAccount, paymentAccount, orgCurrency);
    });
    if (hasCurrencyMismatch) {
      toast.error(t('expenses.form.currencyMismatch', { currency: paymentCurrency }));
      return;
    }
    if (
      isForeignCurrency(paymentAccount?.currency, orgCurrency) &&
      data.line_items.some((item) => typeof item.tax_rate === 'number' && item.tax_rate > 0)
    ) {
      toast.error(t('expenses.form.foreignCurrencyTax', { currency: paymentCurrency }));
      return;
    }

    try {
      // Determine if business_name is a UUID (existing business) or custom text
      const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

      let businessId: string | undefined;
      let customBusinessName: string | undefined;

      if (data.payee_type === 'business') {
        if (data.business_id) {
          businessId = data.business_id;
        } else if (data.business_name) {
          const trimmedName = data.business_name.trim();
          // Only promote a UUID-shaped business_name to a real FK when it
          // actually belongs to one of the known businessOptions. Mirrors the
          // SearchableSelect.onValueChange hardening — without this check a
          // bogus pre-fill (or a future caller passing malformed initialFormData)
          // could write a phantom business_id pointing at a non-existent row.
          // In practice the mapper always sets `business_id` alongside when
          // matched, so this branch only runs for unmatched / free-text input,
          // but belt-and-suspenders is cheap here.
          const isKnownBusiness = businessOptions.some((opt) => opt.value === trimmedName);
          if (isUUID(trimmedName) && isKnownBusiness) {
            businessId = trimmedName;
          } else {
            customBusinessName = trimmedName;
          }
        }

        // Validate that we have either business_id or custom_business_name
        if (!businessId && !customBusinessName) {
          throw new Error('Please select or enter a business name');
        }
      } else if (data.payee_type === 'person') {
        // Validate that we have a person_id
        if (!data.person_id) {
          throw new Error('Please select a person');
        }
      }

      const expenseData = {
        business_id: businessId,
        custom_business_name: customBusinessName,
        person_id: data.payee_type === 'person' ? data.person_id : undefined,
        expense_date: data.expense_date,
        reference: data.reference || undefined,
        description: data.memo || '',  // Must always be a string, not undefined
        payment_account_id: data.payment_account_id,
        project_id: data.project_id === 'none' || !data.project_id ? undefined : data.project_id,
        fund_id: data.fund_id === 'none' || !data.fund_id ? undefined : data.fund_id,
        line_items: data.line_items.map((item, index) => {
          const { tax_rate, tax_treatment } = resolveTaxTreatment(item.tax_rate, item.is_reverse_charge);
          return {
            expense_account_id: item.expense_account_id,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            tax_rate,
            tax_rate_id: item.tax_rate_id || undefined,
            tax_treatment,
            tax_inclusive: item.tax_inclusive,
            exemption_reason: item.exemption_reason || undefined,
            exemption_reason_other: item.exemption_reason_other || undefined,
            sort_order: index,
            // NL WKR: rides the line payload into nl_wkr_sync_from_lines —
            // only when the line's WKR checkbox is on (belt to the uncheck-
            // clears-category handler).
            wkr_category: ((item.wkr_enabled && item.wkr_category) || undefined) as
              | NlWkrCategory
              | undefined,
          };
        }),
      };

      // "Make this recurring": save a TEMPLATE, not an expense. The payload is
      // the exact create wire body minus `expense_date`; the shared chain
      // creates (or PATCHes) it, runs it when due today, and navigates to the
      // list. Carbon / attachments / budget tags are skipped — nothing was
      // created to attach them to. Failures are toasted by the mutations.
      if (data.recurring_enabled && data.recurrence) {
        const payload = buildRecurringPayload('expense', buildExpenseCreateBody(expenseData));
        await submitRecurringTemplate({
          organizationId: selectedOrganization.id,
          documentType: 'expense',
          recurrence: data.recurrence,
          payload,
          templateId: recurringTemplate?.id,
          navigateOnSuccess,
        });
        clearCarbonData();
        setAttachments([]);
        setBudgetCategoriesByLineItem({});
        setBudgetTagsDirty(false);
        form.reset();
        onSuccess?.();
        return;
      }

      // Use update if editing existing expense, otherwise create new
      // Use ref to get stable expenseId value to prevent stale closures during state transitions
      const currentExpenseId = expenseIdRef.current;
      // A declared expense saves the HEADER only (payee, memo, project, fund,
      // reference) through update_expense_header — the frozen lines never
      // leave the form, so the guard has nothing to refuse.
      const isHeaderOnlySave = !!currentExpenseId && isDeclared;
      const result = isHeaderOnlySave
        ? await updateExpenseHeader.mutateAsync({
            organizationId: selectedOrganization.id,
            expenseId: currentExpenseId,
            business_id: expenseData.business_id,
            person_id: expenseData.person_id,
            custom_business_name: expenseData.custom_business_name,
            description: expenseData.description,
            project_id: expenseData.project_id,
            fund_id: expenseData.fund_id,
            reference: expenseData.reference,
          })
        : currentExpenseId
        ? await updateExpense.mutateAsync({
            organizationId: selectedOrganization.id,
            data: { ...expenseData, expense_id: currentExpenseId },
          })
        : await createExpense.mutateAsync({
            organizationId: selectedOrganization.id,
            data: expenseData,
          });

      // Save carbon emissions if there's a ledger transaction. Skipped on a
      // header-only save: carbon derives from the (locked) lines, and the
      // ledger entries it keys on did not change.
      if (result?.ledger_transaction_id && !isHeaderOnlySave) {
        try {
          await saveCarbonEmissions({ transactionId: result.ledger_transaction_id }, data.line_items);
        } catch (carbonError) {
          // Don't fail the whole operation if carbon tracking fails — but SAY
          // so (sibling forms toast + capture; a soft-deleted factor/table now
          // makes this path reachable in normal use, and a silent drop would
          // hide the missing carbon row from both the user and ops).
          console.error('Error saving carbon emissions:', carbonError);
          toast.warning('Expense saved, but carbon emissions could not be saved.');
          captureException(carbonError as Error, {
            context: 'Expense carbon emissions save',
            transactionId: result.ledger_transaction_id,
          });
        }
      }

      // Upload new attachments (only those with file property, not existing files)
      const newFiles = attachments.filter(a => a.file).map(a => a.file);
      if (result?.ledger_transaction_id && newFiles.length > 0) {
        try {
          await uploadAttachments.mutateAsync({
            organizationId: selectedOrganization.id,
            transactionId: result.ledger_transaction_id,
            files: newFiles,
            attachmentType: 'receipt',
            entityType: 'expenses',
            entityId: result.expense_id,
          });
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
          // Don't fail the whole operation if attachment upload fails
        }
      }

      // Save budget category tags. Skipped on a header-only save for the same
      // reason as carbon: the tags key off line ↔ entry pairs that are frozen.
      if (result?.ledger_transaction_id && !isHeaderOnlySave && Object.keys(budgetCategoriesByLineItem).length > 0) {
        try {
          const { data: ledgerEntries } = await supabase
            .from('ledger_entries')
            .select('id, account_id')
            .eq('transaction_id', result.ledger_transaction_id)
            .order('created_at');

          if (ledgerEntries?.length) {
            const tagEntries: { ledger_entry_id: string; budget_category_id: string }[] = [];
            const usedEntryIds = new Set<string>();

            for (const [indexStr, categoryIds] of Object.entries(budgetCategoriesByLineItem)) {
              const lineIndex = parseInt(indexStr, 10);
              const lineItem = data.line_items[lineIndex];
              if (!lineItem || !categoryIds.length) continue;

              const matchingEntry = ledgerEntries.find(
                (e: { id: string; account_id: string }) =>
                  e.account_id === lineItem.expense_account_id && !usedEntryIds.has(e.id)
              );
              if (matchingEntry) {
                usedEntryIds.add(matchingEntry.id);
                for (const catId of categoryIds) {
                  tagEntries.push({ ledger_entry_id: matchingEntry.id, budget_category_id: catId });
                }
              }
            }

            if (tagEntries.length > 0) {
              await saveBudgetCategoryEntries.mutateAsync({
                entries: tagEntries,
                organizationId: selectedOrganization.id,
              });
            }
          }
        } catch (budgetError) {
          console.error('Error saving budget category tags:', budgetError);
        }
      }

      clearCarbonData();
      setAttachments([]);
      setBudgetCategoriesByLineItem({});
      setBudgetTagsDirty(false);

      // Only reset form for NEW expenses, not updates
      // This prevents the form from clearing while dialog is still open
      if (!currentExpenseId) {
        form.reset();
      }

      // Call expense created callback with result data (if provided)
      if (onExpenseCreated && result) {
        await onExpenseCreated(result);
      }

      onSuccess?.();
    } catch (error) {
      console.error(isEditMode ? 'Error updating expense:' : 'Error creating expense:', error);
    }
  };

  // "Make this recurring" ticked/unticked — the host swaps its footer label.
  const handleRecurringToggle = useCallback((enabled: boolean) => {
    onRecurringChange?.(enabled);
  }, [onRecurringChange]);

  // What the schedule seeds from the moment the box is ticked — read live.
  const getRecurrenceSeed = useCallback(() => {
    const values = form.getValues();
    let payee: string | undefined;
    if (values.payee_type === 'business') {
      payee = businessOptions.find((opt) => opt.value === values.business_id)?.label
        ?? (values.business_name && !businessOptions.some((opt) => opt.value === values.business_name)
          ? values.business_name.trim()
          : undefined);
    } else {
      const person = people?.find((p) => p.id === values.person_id);
      payee = person ? `${person.first_name} ${person.last_name}` : undefined;
    }
    return { documentDate: values.expense_date, nameBase: payee || values.memo };
  }, [form, businessOptions, people]);

  // Stable wrapper function that calls the ref. Guards against re-entrant
  // submits (double-click / double-Enter) — the ref stays true for the whole
  // async submit and is always cleared afterward, so a host that doesn't gate
  // its footer button can't post the expense twice.
  const onSubmit = useCallback(async (data: FormData) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmitRef.current?.(data);
    } finally {
      isSubmittingRef.current = false;
    }
  }, []);

  // Expose submit handler to parent - only on mount to prevent re-render loops
  // The handler uses refs internally so it always has access to latest state
  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady(() => form.handleSubmit(onSubmit)());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing expense data when in edit mode. `expense` is live query
  // data, so a background refetch can hand this effect a new object for the
  // SAME id: while the user has unsaved edits that must NOT re-reset the
  // form (it would wipe their input), but when the form is pristine the
  // fresh server data is applied (see loadedExpenseIdRef above).
  // One mapping for edit AND clone hydration (kept in sync by construction).
  const expenseToFormData = useCallback((source: Expense): FormData => {
    // `expense_date` is a plain `date` column, so the server already hands
    // us the calendar day. Slice rather than round-tripping through a JS
    // Date — `new Date('2026-03-15')` is UTC midnight and formats as the
    // 14th for any UTC− browser. The slice also absorbs any legacy
    // timestamptz string still sitting in a cache from before the column
    // conversion.

    // Get payment account from metadata
    const paymentAccountId = String(source.metadata?.payment_account_id ?? '');

    // Determine the value the business SearchableSelect binds to. Options
    // in that select are keyed by business.id (see businessOptions above),
    // so for a linked FK vendor we put the **id** in this field — the
    // select's internal lookup `options.find(opt => opt.value === value)`
    // then finds the option and renders its label. Putting the display
    // name here would fall through to `allowCustomValue` rendering and
    // show an italic "custom text" pill even though the vendor is linked.
    // For the custom-name case we keep the free text — the select renders
    // it as a custom value, matching the submit path that writes it back
    // to `custom_business_name`.
    let businessNameField = '';
    if (source.business_id) {
      businessNameField = source.business_id;
    } else if (source.custom_business_name) {
      businessNameField = source.custom_business_name;
    }

    const formData: FormData = {
      payee_type: source.business_id || source.custom_business_name ? 'business' : 'person',
      business_id: source.business_id || '',
      business_name: businessNameField,
      person_id: source.person_id || '',
      expense_date: source.expense_date.slice(0, 10),
      reference: source.ledger_transaction?.reference || '',
      memo: source.description || '',
      payment_account_id: paymentAccountId,
      project_id: source.project_id || '',
      fund_id: String(source.metadata?.fund_id ?? ''),
      line_items: source.line_items?.map((item: ExpenseLineItem) => {
        const { taxRate, isReverseCharge } = toTaxRateType(item.tax_treatment, item.tax_rate ?? 0);
        return {
          expense_account_id: item.expense_account_id || '',
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: taxRate as TaxRateType,
          tax_rate_id: item.tax_rate_id || '',
          tax_inclusive: item.tax_inclusive ?? false,
          is_reverse_charge: isReverseCharge,
          exemption_reason: item.exemption_reason || '',
          exemption_reason_other: item.exemption_reason_other || '',
          wkr_enabled: !!(item.id && wkrByLineId?.[item.id]),
          wkr_category: (item.id && wkrByLineId?.[item.id]) || '',
        };
      }) || [
        {
          expense_account_id: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          tax_rate: 0 as TaxRateType,
          tax_inclusive: false,
          is_reverse_charge: false,
          exemption_reason: '',
          exemption_reason_other: '',
          // Placeholder 0 — InvoiceLineItems fills the org default rate.
          tax_rate_unset: true,
        },
      ],
    };
    return formData;
  }, [wkrByLineId]);

  useEffect(() => {
    if (isEditMode && expense && accounts && businesses && people && projects && funds && !isLoadingData && !isLoadingWkr) {
      if (loadedExpenseIdRef.current === expense.id && hasUnsavedEditsRef.current) return;
      loadedExpenseIdRef.current = expense.id;
      form.reset(expenseToFormData(expense));
    }
  }, [isEditMode, expense, accounts, businesses, people, projects, funds, isLoadingData, isLoadingWkr, expenseToFormData, form]);

  // Clone mode: same mapping, saved as NEW. The date moves to today, the
  // reference is cleared, and the recurring fields from `defaultValues` are
  // preserved. `reset` makes these the new defaults, so a freshly cloned form
  // is not dirty (hasUserDirtyLeaf stays false until the user acts).
  const clonedFromIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode || !cloneFrom || !accounts || !businesses || !people || !projects || !funds || isLoadingData || isLoadingWkr) return;
    if (clonedFromIdRef.current === cloneFrom.id) return;
    clonedFromIdRef.current = cloneFrom.id;
    const mapped = expenseToFormData(cloneFrom);
    const current = form.getValues();
    form.reset({
      ...current,
      ...mapped,
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      recurring_enabled: current.recurring_enabled,
      recurrence: current.recurrence,
    });
    // "Make recurring" from a row: the box starts ticked with an EMPTY name
    // (the New-menu flow leaves it for the user) — seed it from the cloned
    // payee now that the form holds the cloned values. Still a reset, not a
    // setValue, so the form stays pristine.
    const afterClone = form.getValues();
    if (afterClone.recurring_enabled && afterClone.recurrence && !afterClone.recurrence.name) {
      const seed = getRecurrenceSeed();
      form.reset({
        ...afterClone,
        recurrence: {
          ...afterClone.recurrence,
          name: suggestTemplateName(seed.nameBase, afterClone.recurrence.frequency, (key, vars) => String(t(key, vars))),
        },
      });
    }
  }, [isEditMode, cloneFrom, accounts, businesses, people, projects, funds, isLoadingData, isLoadingWkr, expenseToFormData, form, getRecurrenceSeed, t]);

  // Populate budget category tags from existing data when viewing an expense
  useEffect(() => {
    if (!existingBudgetTags?.length || !expense?.ledger_transaction?.entries) return;

    const entries = expense.ledger_transaction.entries as Array<{ id: string; account_id: string }>;
    const lineItems = expense.line_items ?? [];

    // Build entry ID → line item index map by matching account_id
    const entryIdToIndex = new Map<string, number>();
    const usedEntryIds = new Set<string>();
    lineItems.forEach((li: ExpenseLineItem, idx: number) => {
      const matchingEntry = entries.find(
        (e) => e.account_id === li.expense_account_id && !usedEntryIds.has(e.id)
      );
      if (matchingEntry) {
        entryIdToIndex.set(matchingEntry.id, idx);
        usedEntryIds.add(matchingEntry.id);
      }
    });

    const categories: Record<number, string[]> = {};
    for (const tag of existingBudgetTags) {
      const idx = entryIdToIndex.get(tag.ledger_entry_id);
      if (idx !== undefined) {
        if (!categories[idx]) categories[idx] = [];
        categories[idx].push(tag.budget_category_id);
      }
    }
    setBudgetCategoriesByLineItem(categories);
  }, [existingBudgetTags, expense]);

  const stableAccounts = accounts ?? EMPTY_ACCOUNTS;

  // Filter accounts for payment accounts: Assets, Equity, Liabilities
  // Exclude Accounts Receivable and Accounts Payable (not used for payments)
  const paymentAccounts = useMemo(() => {
    return accounts?.filter((acc: ChartOfAccount) => {
      const typeName = acc.type?.type_name;
      if (typeName === 'Accounts Receivable' || typeName === 'Accounts Payable') {
        return false;
      }
      return (
        acc.category?.category_name === 'Assets' ||
        acc.category?.category_name === 'Equity' ||
        acc.category?.category_name === 'Liabilities' ||
        acc.category?.category_name === "Owner's Equity"
      );
    }) ?? [];
  }, [accounts]);

  // Memoize to prevent new array reference on every render (breaks LineItemRow React.memo)
  const memoizedPinnedAccountIds = useMemo(
    () => suspenseAccountId ? [suspenseAccountId] : undefined,
    [suspenseAccountId]
  );

  return (
    <Form {...form}>
      <div className="space-y-8" data-lpignore="true">
        {/* Metadata row: internal associations (not part of the receipt) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="project_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('expenses.form.projectOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                      <SelectValue placeholder={t('expenses.form.selectProject')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">{t('expenses.form.none')}</SelectItem>
                    {projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.code} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fund_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('expenses.form.fundOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <FundSelect
                  formControl
                  funds={funds ?? []}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={t('expenses.form.selectFund')}
                  specialOptions={[{ value: 'none', label: t('expenses.form.none') }]}
                  className={fieldState.error ? 'border-destructive' : undefined}
                />
              </FormItem>
            )}
          />
        </div>

        {/* Expense document card */}
        <Card>
          <CardContent className="pt-6 space-y-8">
            <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
              {t('expenses.form.expenseTitle')}
            </h2>

            {/* Full-bleed paper band: Payee + Payment Details. Frozen light
                like the AP/AR forms (a receipt is "paper"); the wrapper pins
                text colors so theme-token internals (placeholders, the
                AccountSelect type hint) stay legible in dark mode. */}
            <div className="-mx-6 px-6 py-6 bg-releve-gray-light text-gray-900 [&_.text-muted-foreground]:text-gray-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: PAYEE */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">
                    {t('payee')}
                  </h3>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="payee_type"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.payeeType')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="business">{t('expenses.form.business')}</SelectItem>
                              <SelectItem value="person">{t('expenses.form.person')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    {payeeType === 'business' ? (
                      <FormField
                        control={form.control}
                        name="business_name"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.business')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <SearchableSelect
                                options={businessOptions}
                                value={field.value}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  // Only set business_id when the new value is a UUID
                                  // that actually belongs to one of the known
                                  // businessOptions. Without the `isKnown` check a user
                                  // typing a UUID-shaped string (or a custom value that
                                  // coincidentally matches the regex) could write a
                                  // phantom FK pointing at a non-existent business row.
                                  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
                                  const isKnown = businessOptions.some((opt) => opt.value === value);
                                  if (isUUID && isKnown) {
                                    form.setValue('business_id', value);
                                  } else {
                                    form.setValue('business_id', '');
                                  }
                                }}
                                placeholder={t('expenses.form.selectBusinessPlaceholder')}
                                searchPlaceholder={t('expenses.form.searchBusinesses')}
                                emptyText={t('expenses.form.noBusinessesFound')}
                                allowCustomValue={true}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ) : (
                      <FormField
                        control={form.control}
                        name="person_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.person')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className={`bg-white data-[placeholder]:text-gray-500 ${fieldState.error ? "border-destructive" : ""}`}>
                                  <SelectValue placeholder={t('expenses.form.selectPerson')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {people?.map((person) => (
                                  <SelectItem key={person.id} value={person.id}>
                                    {person.first_name} {person.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </div>

                {/* Right Column: PAYMENT DETAILS */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">
                    {t('expenses.form.paymentDetails')}
                  </h3>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="payment_account_id"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.paymentAccount')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <AccountSelect
                              accounts={paymentAccounts}
                              showCurrency={showAccountCurrency}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={t('expenses.form.selectAccount')}
                              className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              prioritizeType={["Bank", "Credit Card"]}
                              prioritizeCategory="Equity"
                              disabled={isDeclared}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="expense_date"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.paymentDate')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <DatePicker
                              value={parseLocalISODate(field.value)}
                              onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                              // Set by the schedule on a recurring template; frozen
                              // with the VAT lines on a declared expense.
                              disabled={recurringEnabled || isDeclared}
                              className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                            />
                          </FormControl>
                          {recurringEnabled && (
                            // The band already pins `.text-muted-foreground` to gray-500.
                            <p className="text-xs text-muted-foreground mt-1">{t('recurring.form.setBySchedule')}</p>
                          )}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="reference"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('expenses.form.referenceOptional')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              placeholder={t('expenses.form.referencePlaceholder')}
                              className={`bg-white placeholder:text-gray-500 ${fieldState.error ? "border-destructive" : ""}`}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Declared VAT — says which inputs are frozen and why. Sits with
                the conversion notices: both describe a state of the document,
                not a fault, and both precede the line items they qualify. */}
            {isDeclared && (
              <DeclaredVatBanner
                organizationId={selectedOrganization?.id}
                settlementId={declaredSettlementId}
              />
            )}
            {/* Currency conversion notice for scanned foreign-currency documents */}
            {conversionBanner?.type === 'estimated' && (
              <Alert className="bg-info/10 text-info border-info/30">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('documentConversion.estimatedBanner', {
                    currency: conversionBanner.docCurrency,
                    rate: Number(conversionBanner.rate.toPrecision(6)),
                    date: conversionBanner.rateDate,
                  })}
                </AlertDescription>
              </Alert>
            )}
            {conversionBanner?.type === 'unavailable' && (
              <Alert className="bg-warning/10 text-warning border-warning/30">
                <TriangleAlert className="h-4 w-4" />
                <AlertDescription>
                  {t('documentConversion.rateUnavailableBanner', {
                    currency: conversionBanner.docCurrency,
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* SECTION: LINE ITEMS */}
            <InvoiceLineItems
              asOfDate={form.watch('expense_date')}
              type="ap"
              form={form}
              accounts={stableAccounts}
              currency={
                // Rate lookup failed → amounts are still in the document's
                // currency; label them as such rather than with the org symbol.
                conversionBanner?.type === 'unavailable'
                  ? conversionBanner.docCurrency
                  : currency
              }
              carbonDataByLineItem={carbonDataByLineItem}
              onCarbonDataChange={updateCarbonData}
              pinnedAccountIds={memoizedPinnedAccountIds}
              allowTaxInclusive={true}
              isExpense={true}
              suppressDefaultTax={foreignPayment}
              enableWkr
              // Frozen lines: every input, Add and remove are disabled, not
              // hidden — forms disable, panels hide (docs/TAX.md "Edit guard").
              disabled={isDeclared}
              budgetCategoriesByLineItem={budgetCategoriesByLineItem}
              onBudgetCategoryChange={(index, ids) => {
                setBudgetTagsDirty(true);
                setBudgetCategoriesByLineItem((prev) => ({ ...prev, [index]: ids }));
              }}
            />
          </CardContent>
        </Card>

        {/* SECTION: MAKE THIS RECURRING — sits above the attachments so the
            upload zone can be disabled while ticked (attachments are not copied
            to generated documents). Never in document-edit mode. */}
        {(allowRecurring || isTemplateMode) && !isEditMode && (
          <RecurringSection
            documentType="expense"
            orgToday={orgToday}
            getSeed={getRecurrenceSeed}
            template={recurringTemplate}
            onEnabledChange={handleRecurringToggle}
          />
        )}

        {/* SECTION: ADDITIONAL INFO */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {t('expenses.form.additionalInfo')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <FormField
              control={form.control}
              name="memo"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col h-full">
                  <div className="flex items-center gap-2">
                    <FormLabel>{t('expenses.form.memoOptional')}</FormLabel>
                    {fieldState.error && (
                      <span className="text-xs text-destructive">{fieldState.error.message}</span>
                    )}
                  </div>
                  <FormControl className="flex-1">
                    <Textarea
                      placeholder={t('expenses.form.memoPlaceholder')}
                      className={`${fieldState.error ? "border-destructive" : ""} resize-none h-full min-h-[120px]`}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex flex-col h-full min-h-[120px] space-y-2">
              <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('expenses.form.attachmentsOptional')}
              </Label>
              {recurringEnabled && (
                <p className="text-xs text-muted-foreground">{t('recurring.form.notes.notCopied')}</p>
              )}
              <Card className="flex-1 flex flex-col">
                <CardContent className="flex-1 flex flex-col p-4">
                  <FileAttachments
                    disabled={recurringEnabled}
                    value={attachments}
                    onChange={setAttachments}
                    onDeleteExisting={handleDeleteExisting}
                    maxFiles={5}
                    className="flex-1"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {isTemplateMode && onRequestDelete && (
          <DangerZone
            title={t('recurring.dangerZone.heading')}
            actionTitle={t('recurring.dangerZone.title')}
            description={t('recurring.dangerZone.description')}
            buttonLabel={t('recurring.dangerZone.action')}
            onAction={onRequestDelete}
          />
        )}
      </div>
    </Form>
  );
};
